<!-- eslint-disable vue/no-v-html -->
<template>
  <div ref="rootElRef" class="sub-categories">
    <Breadcrumbs :content="content.breadcrumb" />
    <HeroSubcat :content="content.hero"></HeroSubcat>

    <div class="sub-categories__inside">
      <div
        v-if="content.hero.appointmentCta"
        class="sub-categories__inside__cta-wrapper wrapper"
      >
        <AppointmentCard
          class="sub-categories__inside__cta"
          :content="content.hero.appointmentCta"
        ></AppointmentCard>
      </div>

      <div class="sticky-trap"></div>

      <div v-if="content.highlightedProduct" class="wrapper">
        <HighlightedCard :content="content.highlightedProduct" />
      </div>

      <Keypoints
        v-if="content.keypoints && moreThanFour"
        class="sub-categories__keypoints"
        :content="content.keypoints"
      ></Keypoints>

      <HotProduct
        v-if="content.hotProduct"
        :content="content.hotProduct"
      ></HotProduct>

      <!-- Full Version -->
      <div v-if="content.products && content.productsTitle" class="wrapper">
        <GTitle :content="content.productsTitle" tag="h2" class="h2-serif" />
      </div>

      <GridCards
        v-if="content.products"
        :content="content.products"
        type="category"
      ></GridCards>

      <Keypoints
        v-if="content.keypoints && !moreThanFour"
        class="sub-categories__keypoints"
        :content="content.keypoints"
      ></Keypoints>

      <!-- Light Version -->
      <div v-if="content.sections">
        <section
          v-for="(section, index) in content.sections"
          :id="section.slug"
          :key="`section-${index}`"
          class="services__section"
        >
          <GTitle
            :content="section.title"
            tag="h2"
            class="services__section__title"
          />

          <DuoGallery
            v-for="(duo, j) in section.content"
            :key="`cpt-${j}`"
            :content="duo"
          ></DuoGallery>
        </section>
      </div>
    </div>

    <DuoGallery :content="content.brochure"></DuoGallery>

    <CardsSlider :content="content.otherCategories"></CardsSlider>

    <div
      v-if="content.logoTitle"
      class="sub-categories__subtitle-wrapper wrapper"
    >
      <h3 class="sub-categories__subtitle" v-html="content.logoTitle"></h3>
    </div>

    <Brands v-if="content.logos" :content="content.logos"></Brands>

    <FlexibleFaq v-if="content.faqs" :content="content.faqs"></FlexibleFaq>
    <BigCta :content="content.bigCta"></BigCta>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'

import AppointmentCard from '@/components/cards/Appointment.vue'
import HighlightedCard from '@/components/cards/Highlighted.vue'
import DuoGallery from '@/components/duo/Gallery.vue'
import HotProduct from '@/components/duo/HotProduct.vue'
import FlexibleFaq from '@/components/flexible/Faq.vue'
import Keypoints from '@/components/flexible/Keypoints.vue'
import BigCta from '@/components/global/BigCta.vue'
import Brands from '@/components/global/Brands.vue'
import Breadcrumbs from '@/components/global/Breadcrumbs.vue'
import GridCards from '@/components/grid/Cards.vue'
import HeroSubcat from '@/components/hero/Subcategory.vue'
import CardsSlider from '@/components/slider/Cards.vue'

// Props
const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
})

const rootElRef = ref<HTMLElement | null>(null)

let observer: IntersectionObserver

const moreThanFour = computed(
  () => props.content.products && props.content.products.length > 4
)

onMounted(() => {
  // Observe when popup button is sticking
  const closeEl = rootElRef.value?.querySelector('.sticky-trap')
  const sticky = rootElRef.value?.querySelector('.sub-categories__inside__cta')

  if (closeEl && sticky) {
    observer = new IntersectionObserver(
      ([e]) => {
        sticky.classList.toggle('is-sticky', e.intersectionRatio < 1)
      },
      { threshold: [1] }
    )

    observer.observe(closeEl)
  }
})

onUnmounted(() => {
  // Disable observer
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style lang="scss" scoped>
.sub-categories {
  padding-top: $header-height-mobile;

  :deep(.duo-gallery__content__title) {
    @extend %fraunces-spec;
    @extend %fw-light;
  }

  @include mq(l) {
    padding-top: $header-height;
  }
}

.sub-categories__inside {
  position: relative;
}

.sub-categories__inside__cta-wrapper {
  position: absolute;
  top: 3.5rem;
  right: 0;
  height: calc(100% - 7rem);
}

.sub-categories__inside__cta {
  position: fixed;
  z-index: 5;
  right: col(1);
  bottom: 5rem;
  overflow: hidden;
  width: 31rem;
  transition: all 0.2s $ease-in-out;

  :deep(.appointment__link) {
    padding-inline: $spacing;
  }

  @include mq($until: l) {
    display: none;
  }
}

.sub-categories__keypoints {
  margin-bottom: $spacing * 6;
}

.sub-categories__subtitle-wrapper {
  display: flex;
}

.sub-categories__subtitle {
  @extend %fraunces-spec;
  @extend %fw-light;

  margin: auto;
  color: $c-green-abr;
  font-size: 3.8rem;
  line-height: 1;
}

:deep(.appointment__content) {
  opacity: 1;
  scale: 1;
  animation: show-square 0.3s ease-in-out;
}

:deep(.appointment__cta) {
  opacity: 0;
  scale: 0;
  animation: hide-square 0.4s ease-in-out;
}

.sub-categories__inside__cta:not(:hover) {
  &.is-sticky {
    pointer-events: none;

    :deep(.appointment__content) {
      scale: 0;
      opacity: 0;
      animation: hide-square 0.4s ease-in-out;
    }

    :deep(.appointment__cta) {
      scale: 1;
      opacity: 1;
      animation: show-square 0.3s ease-in-out;
    }
  }
}

.services__section {
  // prettier-ignore
  @include fluid(padding-block, (xxs: 4.8rem, xxl: 12rem));

  border-bottom: 1px solid $c-gray-lighter;

  &:last-of-type {
    border-bottom: 0;
  }
}

.services__section__title {
  max-width: 60rem;
  margin: 0 auto $spacing * 0.5;
  color: $c-green-abr;
  font-weight: 300;
  text-align: center;
}

@keyframes show-square {
  0% {
    scale: 0;
    opacity: 0;
  }

  100% {
    scale: 1;
    opacity: 1;
  }
}

@keyframes hide-square {
  0% {
    scale: 1;
    opacity: 1;
  }

  100% {
    scale: 0;
    opacity: 0;
  }
}
</style>
